import { useFormik } from 'formik'
import * as Yup from 'yup'
import { EventHandler, KeyboardEvent, useCallback } from 'react'
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params'

type UseProgramNameSearchProps<T extends QueryParamConfigMap> = {
  queryParams: DecodedValueMap<T>
  setQueryParams: SetQuery<T>
}

export const useProgramNameSearch = <T extends QueryParamConfigMap>({
  queryParams,
  setQueryParams
}: UseProgramNameSearchProps<T>) => {
  const { values, setFieldValue, handleChange, submitForm, validateForm } = useFormik({
    initialValues: {
      programName: queryParams.search
    },
    validateOnChange: false,
    validationSchema: () =>
      Yup.object().shape({
        programName: Yup.string()
      }),
    onSubmit: ({ programName }) =>
      setQueryParams({ search: programName?.trim() || undefined } as Partial<DecodedValueMap<T>>)
  })

  const handleClearProgramNameValue = () => {
    setFieldValue('programName', '')
    submitForm()
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') submitForm()

      if (event.key === 'Escape') setFieldValue('programName', '')
    },
    [setFieldValue, submitForm]
  )

  return {
    programName: values.programName,
    programNameSearch: {
      onProgramNameChange: handleChange,
      handleClearProgramNameValue,
      handleKeyPress,
      validateProgramName: () => validateForm()
    }
  }
}
